import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import ReactApexChart from "react-apexcharts";
import axiosInstance from "../config/axiosInstance";
import { CRM, DASHBOARD } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";

const pad2 = (value) => String(value).padStart(2, "0");

const formatDateKey = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const buildDefaultRange = () => {
  const today = new Date();
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    startDate: formatDateKey(start),
    endDate: formatDateKey(end),
  };
};

const fetchOperationsDashboard = ({ startDate, endDate, funnelIds = [] }) => {
  const params = { startDate, endDate };
  if (funnelIds.length) {
    params.funnelIds = funnelIds;
  }

  return axiosInstance
    .get(DASHBOARD.GET_OPERATIONS, { params })
    .then((response) => response.data.data);
};

const fetchFunnels = () =>
  axiosInstance
    .get(CRM.FUNNELS)
    .then((response) => response.data.data ?? [])
    .then((list) =>
      Array.isArray(list) ? list.filter((funnel) => funnel?.isMetrified) : [],
    );

export default function UserRoutineMetricsPage() {
  const { userPermission } = useApplication();
  const isPowerUser = String(userPermission || "").includes("POWER_USER");

  const [range, setRange] = useState(buildDefaultRange);
  const [selectedFunnelIds, setSelectedFunnelIds] = useState([]);
  const [funnelSearch, setFunnelSearch] = useState("");
  const [isFunnelAutocompleteOpen, setIsFunnelAutocompleteOpen] =
    useState(false);

  const {
    data: funnels = [],
    isLoading: isLoadingFunnels,
    isError: isFunnelsError,
    error: funnelsError,
  } = useQuery("crmFunnels", fetchFunnels, {
    enabled: isPowerUser,
  });

  useEffect(() => {
    if (!selectedFunnelIds.length && funnels.length) {
      setSelectedFunnelIds([String(funnels[0].id)]);
    }
  }, [funnels, selectedFunnelIds.length]);

  const { data, isLoading, isError, error, refetch } = useQuery(
    [
      "routineMetricsDashboard",
      range.startDate,
      range.endDate,
      selectedFunnelIds.join(","),
    ],
    () =>
      fetchOperationsDashboard({
        startDate: range.startDate,
        endDate: range.endDate,
        funnelIds: selectedFunnelIds,
      }),
    {
      enabled: isPowerUser && Boolean(range.startDate && range.endDate),
    },
  );

  const taskCompletion = data?.taskCompletion ?? [];

  const userRoutineMetrics = useMemo(
    () =>
      taskCompletion
        .map((user) => {
          const totalTasks = Number(user?.totalTasks ?? 0);
          const completedTasks = Number(user?.completedTasks ?? 0);
          const completionRate = Number(user?.completionRate ?? 0);
          const pendingTasks = Math.max(totalTasks - completedTasks, 0);

          return {
            ...user,
            totalTasks,
            completedTasks,
            completionRate,
            pendingTasks,
            userKey: user?.userId
              ? `id:${String(user.userId)}`
              : `name:${String(user?.userName || "")}`,
          };
        })
        .sort((a, b) => {
          if (b.completedTasks !== a.completedTasks) {
            return b.completedTasks - a.completedTasks;
          }
          return b.completionRate - a.completionRate;
        }),
    [taskCompletion],
  );
  const [selectedUserKey, setSelectedUserKey] = useState("");

  const teamTotals = useMemo(
    () => ({
      users: userRoutineMetrics.length,
      assigned: userRoutineMetrics.reduce(
        (sum, user) => sum + Number(user.totalTasks || 0),
        0,
      ),
      completed: userRoutineMetrics.reduce(
        (sum, user) => sum + Number(user.completedTasks || 0),
        0,
      ),
      pending: userRoutineMetrics.reduce(
        (sum, user) => sum + Number(user.pendingTasks || 0),
        0,
      ),
      averageRate: userRoutineMetrics.length
        ? Math.round(
            userRoutineMetrics.reduce(
              (sum, user) => sum + Number(user.completionRate || 0),
              0,
            ) / userRoutineMetrics.length,
          )
        : 0,
    }),
    [userRoutineMetrics],
  );

  useEffect(() => {
    if (!userRoutineMetrics.length) {
      setSelectedUserKey("");
      return;
    }

    const hasSelected = userRoutineMetrics.some(
      (user) => user.userKey === selectedUserKey,
    );
    if (!selectedUserKey || !hasSelected) {
      setSelectedUserKey(userRoutineMetrics[0].userKey);
    }
  }, [userRoutineMetrics, selectedUserKey]);

  const selectedUser = useMemo(
    () =>
      userRoutineMetrics.find((user) => user.userKey === selectedUserKey) ||
      null,
    [userRoutineMetrics, selectedUserKey],
  );

  const periodDays = useMemo(() => {
    if (!range.startDate || !range.endDate) return 1;
    const start = new Date(`${range.startDate}T00:00:00`);
    const end = new Date(`${range.endDate}T00:00:00`);
    const diffMs = end.getTime() - start.getTime();
    if (!Number.isFinite(diffMs) || diffMs < 0) return 1;
    return Math.floor(diffMs / 86400000) + 1;
  }, [range.startDate, range.endDate]);

  const selectedUserDetails = useMemo(() => {
    if (!selectedUser) {
      return null;
    }

    const assignedShare = teamTotals.assigned
      ? (selectedUser.totalTasks / teamTotals.assigned) * 100
      : 0;
    const completedShare = teamTotals.completed
      ? (selectedUser.completedTasks / teamTotals.completed) * 100
      : 0;
    const pendingShare = teamTotals.pending
      ? (selectedUser.pendingTasks / teamTotals.pending) * 100
      : 0;
    const pendingRate = selectedUser.totalTasks
      ? (selectedUser.pendingTasks / selectedUser.totalTasks) * 100
      : 0;
    const throughputPerDay = selectedUser.completedTasks / periodDays;
    const backlogDaysEstimate =
      throughputPerDay > 0
        ? selectedUser.pendingTasks / throughputPerDay
        : null;
    const ranking = userRoutineMetrics.findIndex(
      (user) => user.userKey === selectedUser.userKey,
    );

    return {
      assignedShare,
      completedShare,
      pendingShare,
      pendingRate,
      throughputPerDay,
      backlogDaysEstimate,
      ranking: ranking >= 0 ? ranking + 1 : null,
      completionDeltaVsTeam:
        selectedUser.completionRate - teamTotals.averageRate,
    };
  }, [selectedUser, teamTotals, periodDays, userRoutineMetrics]);

  const teamCompletionRate = useMemo(() => {
    if (!teamTotals.assigned) {
      return 0;
    }
    return (teamTotals.completed / teamTotals.assigned) * 100;
  }, [teamTotals.assigned, teamTotals.completed]);

  const teamWorkloadSeries = useMemo(
    () => [
      {
        name: "Concluídas",
        data: userRoutineMetrics.map((user) => user.completedTasks),
      },
      {
        name: "Pendentes",
        data: userRoutineMetrics.map((user) => user.pendingTasks),
      },
    ],
    [userRoutineMetrics],
  );

  const teamWorkloadOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 6,
          barHeight: "58%",
        },
      },
      xaxis: {
        categories: userRoutineMetrics.map(
          (user) => user.userName || "Usuário",
        ),
        labels: { style: { fontSize: "11px" } },
      },
      yaxis: { labels: { style: { fontSize: "11px" } } },
      colors: ["#059669", "#F59E0B"],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      grid: { borderColor: "#EEF2F7" },
      tooltip: {
        y: {
          formatter: (value) => `${Math.round(value)} tarefas`,
        },
      },
    }),
    [userRoutineMetrics],
  );

  const selectedUserDonutSeries = useMemo(() => {
    if (!selectedUser) return [0, 0];
    return [selectedUser.completedTasks, selectedUser.pendingTasks];
  }, [selectedUser]);

  const selectedUserDonutOptions = useMemo(
    () => ({
      chart: { type: "donut", toolbar: { show: false } },
      labels: ["Concluídas", "Pendentes"],
      colors: ["#10B981", "#F59E0B"],
      legend: { position: "bottom" },
      dataLabels: {
        enabled: true,
        formatter: (value) => `${value.toFixed(0)}%`,
      },
      stroke: { colors: ["#ffffff"] },
      tooltip: {
        y: {
          formatter: (value) => `${Math.round(value)} tarefas`,
        },
      },
    }),
    [],
  );

  const toggleFunnel = (funnelId) => {
    setSelectedFunnelIds((prev) => {
      if (prev.includes(funnelId)) {
        return prev.filter((id) => id !== funnelId);
      }
      return [...prev, funnelId];
    });
  };

  const selectedFunnels = useMemo(
    () =>
      selectedFunnelIds
        .map((id) => funnels.find((funnel) => String(funnel.id) === id))
        .filter(Boolean),
    [selectedFunnelIds, funnels],
  );

  const filteredFunnels = useMemo(() => {
    const term = funnelSearch.trim().toLowerCase();
    const available = funnels.filter(
      (funnel) => !selectedFunnelIds.includes(String(funnel.id)),
    );

    if (!term) {
      return available.slice(0, 8);
    }

    return available
      .filter((funnel) =>
        String(funnel.name || "")
          .toLowerCase()
          .includes(term),
      )
      .slice(0, 8);
  }, [funnelSearch, funnels, selectedFunnelIds]);

  const handleFunnelAutocompleteSelect = (funnelId) => {
    toggleFunnel(String(funnelId));
    setFunnelSearch("");
    setIsFunnelAutocompleteOpen(false);
  };

  if (!isPowerUser) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <h1 className="text-base font-semibold text-gray-700">
          Métricas de Rotina
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Esta seção é restrita a usuários com permissão POWER_USER.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            Administração
          </p>
          <h1 className="text-lg font-bold text-gray-900 mt-0.5">
            Rotinas e métricas por usuário
          </h1>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-stretch divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
          {/* Date range */}
          <div className="flex items-center gap-2 px-4 py-3 shrink-0">
            <div className="flex flex-col gap-0.5">
              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                De
              </span>
              <input
                type="date"
                value={range.startDate}
                max={range.endDate}
                onChange={(e) =>
                  setRange((prev) => ({ ...prev, startDate: e.target.value }))
                }
                className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 focus:bg-white focus:border-gray-400 focus:outline-none transition"
              />
            </div>
            <span className="text-gray-200 text-sm mt-4 select-none">→</span>
            <div className="flex flex-col gap-0.5">
              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                Até
              </span>
              <input
                type="date"
                value={range.endDate}
                min={range.startDate}
                onChange={(e) =>
                  setRange((prev) => ({ ...prev, endDate: e.target.value }))
                }
                className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 focus:bg-white focus:border-gray-400 focus:outline-none transition"
              />
            </div>
          </div>

          {/* Funnel selector */}
          <div className="flex flex-col gap-2 px-4 py-3 flex-1 min-w-0">
            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
              Funis metrificados
            </span>
            {isLoadingFunnels ? (
              <p className="text-xs text-gray-400">Carregando funis...</p>
            ) : isFunnelsError ? (
              <p className="text-xs text-red-500">
                {funnelsError?.message || "Erro ao carregar"}
              </p>
            ) : funnels.length ? (
              <div className="flex flex-col gap-2">
                <div
                  className="relative"
                  onFocus={() => setIsFunnelAutocompleteOpen(true)}
                  onBlur={() =>
                    setTimeout(() => setIsFunnelAutocompleteOpen(false), 120)
                  }
                >
                  <input
                    type="text"
                    value={funnelSearch}
                    onChange={(e) => {
                      setFunnelSearch(e.target.value);
                      setIsFunnelAutocompleteOpen(true);
                    }}
                    placeholder="Buscar funil..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-gray-50 focus:bg-white focus:border-gray-400 focus:outline-none transition"
                  />
                  {isFunnelAutocompleteOpen && filteredFunnels.length ? (
                    <div className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      {filteredFunnels.map((funnel) => (
                        <button
                          key={String(funnel.id)}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleFunnelAutocompleteSelect(String(funnel.id));
                          }}
                          className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                        >
                          {funnel.name}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selectedFunnels.length ? (
                    selectedFunnels.map((funnel) => (
                      <span
                        key={String(funnel.id)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-600"
                      >
                        {funnel.name}
                        <button
                          type="button"
                          onClick={() => toggleFunnel(String(funnel.id))}
                          className="text-gray-400 hover:text-gray-700 transition"
                        >
                          ×
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-[11px] text-gray-400">
                      Nenhum funil selecionado.
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400">
                Nenhum funil metrificado encontrado.
              </p>
            )}
          </div>

          {/* User selector */}
          <div className="flex flex-col gap-2 px-4 py-3 shrink-0 min-w-[180px]">
            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
              Usuário
            </span>
            <select
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 focus:bg-white focus:border-gray-400 focus:outline-none transition disabled:opacity-50"
              value={selectedUserKey}
              onChange={(e) => setSelectedUserKey(e.target.value)}
              disabled={isLoading || !userRoutineMetrics.length}
            >
              {!userRoutineMetrics.length && (
                <option value="">Sem usuários no período</option>
              )}
              {userRoutineMetrics.map((user) => (
                <option key={user.userKey} value={user.userKey}>
                  {user.userName}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400 leading-tight">
              Análise detalhada abaixo.
            </p>
          </div>
        </div>
      </div>

      {/* Team KPIs */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <KpiCard
          accent="bg-blue-400"
          label="Usuários monitorados"
          value={teamTotals.users}
          note="Com tarefas no período"
        />
        <KpiCard
          accent="bg-indigo-400"
          label="Tarefas atribuídas"
          value={teamTotals.assigned}
          note="Carga total distribuída"
        />
        <KpiCard
          accent="bg-emerald-400"
          label="Tarefas concluídas"
          value={teamTotals.completed}
          note={`${teamCompletionRate.toFixed(1)}% de execução`}
          valueClass="text-emerald-600"
        />
        <KpiCard
          accent="bg-amber-400"
          label="Backlog atual"
          value={teamTotals.pending}
          note={`Média: ${teamTotals.averageRate}% de conclusão`}
          valueClass="text-amber-500"
        />
      </div>

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Team bar chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2.5 pb-3 mb-1 border-b border-gray-100">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500 shrink-0 text-xs font-bold">
              ≡
            </span>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">
                Comparativo do time
              </h2>
              <p className="text-[11px] text-gray-400 leading-tight">
                Concluídas e pendentes por usuário.
              </p>
            </div>
          </div>
          {userRoutineMetrics.length ? (
            <div className="mt-3">
              <ReactApexChart
                options={teamWorkloadOptions}
                series={teamWorkloadSeries}
                type="bar"
                height={Math.max(280, userRoutineMetrics.length * 46)}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg mt-3">
              Sem dados para montar o comparativo.
            </div>
          )}
        </div>

        {/* User donut */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col">
          <div className="flex items-center gap-2.5 pb-3 mb-1 border-b border-gray-100">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 shrink-0 text-xs font-bold">
              ◎
            </span>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">
                Perfil do usuário
              </h2>
              <p className="text-[11px] text-gray-400 leading-tight">
                Concluídas vs pendentes.
              </p>
            </div>
          </div>
          {selectedUser ? (
            <>
              <div className="mt-2 flex-1">
                <ReactApexChart
                  options={selectedUserDonutOptions}
                  series={selectedUserDonutSeries}
                  type="donut"
                  height={220}
                />
              </div>
              <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-gray-800">
                    {selectedUser.userName}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Taxa: {selectedUser.completionRate}%
                  </p>
                </div>
                <span className="text-[10px] font-bold text-gray-500 bg-white border border-gray-200 rounded-full px-2 py-0.5">
                  #{selectedUserDetails?.ranking || "—"} de {teamTotals.users}
                </span>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center flex-1 h-48 text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg mt-3">
              Selecione um usuário.
            </div>
          )}
        </div>
      </div>

      {/* User detail */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2.5">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-100 text-gray-500 shrink-0 text-xs font-bold">
            ✦
          </span>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">
              Detalhamento do usuário
            </h2>
            <p className="text-[11px] text-gray-400 leading-tight">
              Execução, carga e eficiência no período.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-400">
            Carregando métricas...
          </div>
        ) : isError ? (
          <div className="p-8 text-center text-sm text-red-500">
            Falha ao carregar: {error?.message || "erro desconhecido"}
          </div>
        ) : selectedUser && selectedUserDetails ? (
          <div className="p-5 flex flex-col gap-4">
            {/* User identity */}
            <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-600 text-sm font-bold uppercase select-none">
                {(selectedUser.userName || "?")[0]}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {selectedUser.userName}
                </p>
                <p className="text-[11px] text-gray-400">
                  Posição {selectedUserDetails.ranking || "—"} de{" "}
                  {teamTotals.users} · {periodDays} dia(s) analisado(s)
                </p>
              </div>
              <span
                className={`ml-auto shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${
                  selectedUserDetails.completionDeltaVsTeam >= 0
                    ? "bg-emerald-50 text-emerald-600"
                    : "bg-red-50 text-red-500"
                }`}
              >
                {selectedUserDetails.completionDeltaVsTeam >= 0 ? "+" : ""}
                {selectedUserDetails.completionDeltaVsTeam.toFixed(1)} p.p. vs
                time
              </span>
            </div>

            {/* Stat grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatCard
                label="Tarefas atribuídas"
                value={selectedUser.totalTasks}
                sub={`${selectedUserDetails.assignedShare.toFixed(1)}% da carga`}
              />
              <StatCard
                label="Concluídas"
                value={selectedUser.completedTasks}
                sub={`${selectedUserDetails.completedShare.toFixed(1)}% das conclusões`}
                valueClass="text-emerald-600"
              />
              <StatCard
                label="Pendentes"
                value={selectedUser.pendingTasks}
                sub={`${selectedUserDetails.pendingShare.toFixed(1)}% do backlog`}
                valueClass="text-amber-500"
              />
              <StatCard
                label="Taxa de conclusão"
                value={`${selectedUser.completionRate}%`}
                sub="do total atribuído"
                valueClass={
                  selectedUser.completionRate >= teamTotals.averageRate
                    ? "text-emerald-600"
                    : "text-red-500"
                }
              />
              <StatCard
                label="Ritmo diário"
                value={`${selectedUserDetails.throughputPerDay.toFixed(2)}/dia`}
                sub={`em ${periodDays} dia(s)`}
              />
              <StatCard
                label="Estimativa p/ zerar"
                value={
                  selectedUserDetails.backlogDaysEstimate == null
                    ? "—"
                    : `${Math.ceil(selectedUserDetails.backlogDaysEstimate)}d`
                }
                sub="baseado no ritmo atual"
              />
            </div>

            {/* Progress bars */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-gray-700">
                    Eficiência operacional
                  </p>
                  <span className="text-xs font-bold text-emerald-600">
                    {selectedUser.completionRate}%
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 mb-3">
                  Tarefas concluídas do total atribuído
                </p>
                <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${selectedUser.completionRate}%` }}
                  />
                </div>
                <div className="mt-2 flex justify-between text-[10px] text-gray-400">
                  <span>{selectedUser.completedTasks} concluídas</span>
                  <span>{selectedUser.pendingTasks} pendentes</span>
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-gray-700">
                    Risco de backlog
                  </p>
                  <span className="text-xs font-bold text-amber-500">
                    {selectedUserDetails.pendingRate.toFixed(1)}%
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 mb-3">
                  Percentual de tarefas ainda pendentes
                </p>
                <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-amber-400 transition-all duration-500"
                    style={{
                      width: `${selectedUserDetails.pendingRate.toFixed(1)}%`,
                    }}
                  />
                </div>
                <div className="mt-2 flex justify-between text-[10px] text-gray-400">
                  <span>Taxa de pendência</span>
                  <span>{selectedUserDetails.pendingRate.toFixed(1)}%</span>
                </div>
              </div>
            </div>

            {/* Team context */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">
                  Time — atribuídas
                </p>
                <p className="text-base font-bold text-gray-800 mt-0.5">
                  {teamTotals.assigned}
                </p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">
                  Time — concluídas
                </p>
                <p className="text-base font-bold text-emerald-600 mt-0.5">
                  {teamTotals.completed}
                </p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">
                  Média do time
                </p>
                <p className="text-base font-bold text-gray-800 mt-0.5">
                  {teamTotals.averageRate}%
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-gray-400">
            Nenhum dado encontrado para o período selecionado.
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ accent, label, value, note, valueClass = "text-gray-800" }) {
  return (
    <div className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
      <div className={`h-1 w-full ${accent}`} />
      <div className="p-4">
        <p className="text-[9px] font-bold text-gray-400 tracking-widest uppercase mb-1">
          {label}
        </p>
        <p className={`text-2xl font-bold leading-none ${valueClass}`}>
          {value}
        </p>
        <p className="text-[10px] text-gray-400 mt-1.5">{note}</p>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, valueClass = "text-gray-800" }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4">
      <p className="text-[9px] font-bold text-gray-400 tracking-widest uppercase">
        {label}
      </p>
      <p className={`text-xl font-bold mt-1 leading-none ${valueClass}`}>
        {value}
      </p>
      <p className="text-[10px] text-gray-400 mt-1.5 leading-tight">{sub}</p>
    </div>
  );
}
